import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, BellRing, Send, Eye, Clock, CheckCircle2, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const outcomeColors = {
  sent: "bg-emerald-100 text-emerald-900 border-emerald-200",
  skipped_opted_out: "bg-amber-100 text-amber-900 border-amber-200",
  skipped_cooldown: "bg-slate-100 text-slate-900 border-slate-200",
  skipped_already_submitted: "bg-slate-100 text-slate-900 border-slate-200",
  failed: "bg-rose-100 text-rose-900 border-rose-200",
};

export default function AdminRemindersPage() {
  const [tab, setTab] = useState("send");
  // Send tab
  const [filterText, setFilterText] = useState("");
  const [workerIds, setWorkerIds] = useState("");
  const [worksiteIds, setWorksiteIds] = useState("");
  const [channels, setChannels] = useState({ email: true, in_app: true });
  const [preview, setPreview] = useState(null);
  const [sendBusy, setSendBusy] = useState(false);
  const [lastDispatch, setLastDispatch] = useState(null);
  // iter-2 — Bug C confirm modal state. { target_count, max_without_confirm }
  // or null when the dialog is closed.
  const [confirmGate, setConfirmGate] = useState(null);
  // Settings tab
  const [settings, setSettings] = useState(null);
  const [settingsBusy, setSettingsBusy] = useState(false);
  // Log tab
  const [logs, setLogs] = useState({ items: [], total: 0, page: 1, page_size: 20 });
  const [logFilter, setLogFilter] = useState({ worker_id: "", outcome: "", trigger_type: "" });
  const [logBusy, setLogBusy] = useState(false);

  const buildFilterBody = useCallback(() => {
    const body = {};
    const w = workerIds.split(",").map((x) => x.trim()).filter(Boolean);
    const s = worksiteIds.split(",").map((x) => x.trim()).filter(Boolean);
    if (w.length) body.worker_ids = w;
    if (s.length) body.worksite_ids = s;
    const picked = Object.entries(channels).filter(([, v]) => v).map(([k]) => k);
    if (picked.length) body.channels = picked;
    return body;
  }, [workerIds, worksiteIds, channels]);

  const doPreview = async () => {
    try {
      const { data } = await api.post("/tenant/reminders/preview", buildFilterBody());
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Core dispatch — accepts an explicit `confirm` so the modal retry
  // can call us with { confirm: true }. On 400 `confirm_required` we
  // open the modal instead of toasting; everything else toasts normally.
  const doDispatch = async (confirm = false) => {
    setSendBusy(true);
    try {
      const body = buildFilterBody();
      if (confirm) body.confirm = true;
      const { data } = await api.post("/tenant/reminders/dispatch", body);
      setLastDispatch(data);
      setConfirmGate(null);
      toast.success(`Dispatched ${data.dispatched} reminder${data.dispatched === 1 ? "" : "s"}`);
    } catch (e) {
      // iter-3 (P2.2): the tenant and partner dispatch surfaces both
      // return a FLAT confirm_required body — top-level `error`,
      // `target_count`, `max_without_confirm`, no `detail` wrapper.
      // We still fall back to `response.data.detail` so a redeploy
      // mid-flight (old backend + new SPA) doesn't lose the modal.
      const data = e?.response?.data;
      const err =
        data && typeof data === "object"
          ? (data.error ?? data?.detail?.error)
          : null;
      if (e?.response?.status === 400 && err === "confirm_required") {
        const payload =
          data && typeof data === "object" && typeof data.error === "string"
            ? data
            : data?.detail;
        setConfirmGate({
          target_count: payload.target_count,
          max_without_confirm: payload.max_without_confirm,
        });
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setSendBusy(false);
    }
  };

  const loadSettings = useCallback(async () => {
    setSettingsBusy(true);
    try {
      const { data } = await api.get("/tenant/reminders/settings");
      setSettings(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSettingsBusy(false);
    }
  }, []);

  const saveSettings = async (patch) => {
    setSettingsBusy(true);
    try {
      const { data } = await api.patch("/tenant/reminders/settings", patch);
      setSettings(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSettingsBusy(false);
    }
  };

  const loadLogs = useCallback(async (page = 1) => {
    setLogBusy(true);
    try {
      const q = new URLSearchParams({ page: String(page), page_size: "20" });
      if (logFilter.worker_id) q.set("worker_id", logFilter.worker_id);
      if (logFilter.outcome) q.set("outcome", logFilter.outcome);
      if (logFilter.trigger_type) q.set("trigger_type", logFilter.trigger_type);
      const { data } = await api.get(`/tenant/reminders/logs?${q.toString()}`);
      setLogs(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLogBusy(false);
    }
  }, [logFilter]);

  useEffect(() => {
    if (tab === "settings") loadSettings();
    if (tab === "logs") loadLogs(1);
  }, [tab, loadSettings, loadLogs]);

  return (
    <div className="space-y-6" data-testid="admin-reminders-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Administration</div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Timesheet reminders</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Nudge workers who haven't submitted this week. Fires automatically Friday 5 PM, or push on demand.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="reminders-tabs">
          <TabsTrigger value="send" data-testid="reminders-tab-send">
            <Send className="mr-2 h-4 w-4" /> Send
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="reminders-tab-settings">
            <BellRing className="mr-2 h-4 w-4" /> Settings
          </TabsTrigger>
          <TabsTrigger value="logs" data-testid="reminders-tab-logs">
            <Clock className="mr-2 h-4 w-4" /> Log
          </TabsTrigger>
        </TabsList>

        <TabsContent value="send" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Send a reminder now</CardTitle>
              <CardDescription>
                No filters = everyone with an open timesheet this week. Comma-separated ids narrow the set.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <Label htmlFor="worker-ids">Worker ids</Label>
                  <Textarea
                    id="worker-ids" value={workerIds}
                    onChange={(e) => setWorkerIds(e.target.value)}
                    placeholder="uuid-1, uuid-2"
                    rows={2}
                    data-testid="reminders-worker-ids"
                  />
                </div>
                <div>
                  <Label htmlFor="worksite-ids">Worksite ids</Label>
                  <Textarea
                    id="worksite-ids" value={worksiteIds}
                    onChange={(e) => setWorksiteIds(e.target.value)}
                    placeholder="uuid-a"
                    rows={2}
                    data-testid="reminders-worksite-ids"
                  />
                </div>
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm">
                  <Switch
                    checked={channels.email}
                    onCheckedChange={(v) => setChannels({ ...channels, email: v })}
                    data-testid="reminders-chan-email"
                  />
                  Email
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Switch
                    checked={channels.in_app}
                    onCheckedChange={(v) => setChannels({ ...channels, in_app: v })}
                    data-testid="reminders-chan-inapp"
                  />
                  In-app
                </label>
              </div>

              <div className="flex items-center gap-2 border-t pt-4">
                <Button variant="outline" onClick={doPreview} data-testid="reminders-preview-btn">
                  <Eye className="mr-2 h-4 w-4" /> Preview targets
                </Button>
                <Button onClick={() => doDispatch(false)} disabled={sendBusy} data-testid="reminders-dispatch-btn">
                  {sendBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Send reminder
                </Button>
              </div>

              {preview && (
                <div className="rounded-lg border bg-muted/40 p-4 text-sm" data-testid="reminders-preview-panel">
                  <div className="font-medium text-primary">
                    {preview.total_count ?? preview.count} worker{(preview.total_count ?? preview.count) === 1 ? "" : "s"} would receive a reminder
                  </div>
                  {preview.truncated && (
                    <div className="mt-1 text-xs text-muted-foreground" data-testid="preview-truncated-hint">
                      Showing first {preview.targets.length} of {preview.total_count}.
                    </div>
                  )}
                  {preview.targets.length > 0 && (
                    <ul className="mt-2 max-h-48 overflow-y-auto text-xs text-muted-foreground">
                      {preview.targets.slice(0, 20).map((t) => (
                        <li key={t.user_id} data-testid={`preview-target-${t.user_id}`}>
                          {t.name} <span className="opacity-60">· {t.email}</span>
                        </li>
                      ))}
                      {preview.targets.length > 20 && <li className="italic">… and {preview.targets.length - 20} more</li>}
                    </ul>
                  )}
                </div>
              )}

              {lastDispatch && (
                <div className="rounded-lg border bg-primary/5 p-4 text-sm" data-testid="reminders-dispatch-result">
                  <div className="flex items-center gap-2 font-medium">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    Dispatched {lastDispatch.dispatched}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {Object.entries(lastDispatch.skipped).map(([k, v]) =>
                      v > 0 && <span key={k}>Skipped <strong>{k}</strong>: {v}</span>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Scheduled reminders</CardTitle>
              <CardDescription>Automatic Friday 5 PM (tenant-local) push to anyone with an open sheet.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {settingsBusy && !settings && <Loader2 className="h-4 w-4 animate-spin" />}
              {settings && (
                <>
                  <div className="flex items-center justify-between rounded-xl border p-4">
                    <div>
                      <div className="font-medium">Scheduled reminders</div>
                      <div className="text-xs text-muted-foreground">
                        Current cron: <code className="font-mono">{settings.scheduled_cron_local}</code>
                      </div>
                      {settings.next_run_at && (
                        <div className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid="reminders-next-run-at">
                          <CalendarClock className="h-3 w-3" />
                          Next run: <span className="font-mono">{settings.next_run_at}</span>
                        </div>
                      )}
                    </div>
                    <Switch
                      checked={settings.scheduled_enabled}
                      onCheckedChange={(v) => saveSettings({ scheduled_enabled: v })}
                      disabled={settingsBusy}
                      data-testid="reminders-setting-enabled"
                    />
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <Label htmlFor="cron">Local cron</Label>
                      <Input
                        id="cron"
                        value={settings.scheduled_cron_local}
                        onChange={(e) => setSettings({ ...settings, scheduled_cron_local: e.target.value })}
                        onBlur={(e) => saveSettings({ scheduled_cron_local: e.target.value })}
                        disabled={settingsBusy}
                        data-testid="reminders-setting-cron"
                      />
                      <p className="mt-1 text-xs text-muted-foreground">`m h dom mon dow` — default `0 17 * * 5` (Fri 5 PM).</p>
                    </div>
                    <div>
                      <Label htmlFor="cooldown">Manual cooldown (minutes)</Label>
                      <Input
                        id="cooldown"
                        type="number" min={0} max={1440}
                        value={settings.manual_cooldown_minutes}
                        onChange={(e) => setSettings({ ...settings, manual_cooldown_minutes: Number(e.target.value) })}
                        onBlur={(e) => saveSettings({ manual_cooldown_minutes: Number(e.target.value) })}
                        disabled={settingsBusy}
                        data-testid="reminders-setting-cooldown"
                      />
                      <p className="mt-1 text-xs text-muted-foreground">How long before you can retry a manual send to the same worker.</p>
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="max-targets">Confirm threshold (workers)</Label>
                      <Input
                        id="max-targets"
                        type="number" min={0} max={100000}
                        value={settings.max_targets_without_confirm ?? 25}
                        onChange={(e) => setSettings({ ...settings, max_targets_without_confirm: Number(e.target.value) })}
                        onBlur={(e) => saveSettings({ max_targets_without_confirm: Number(e.target.value) })}
                        disabled={settingsBusy}
                        data-testid="reminders-setting-max-targets"
                      />
                      <p className="mt-1 text-xs text-muted-foreground">
                        If a manual dispatch would reach more than this many workers, we'll pop a confirm. Set to 0 to require confirm on every manual send.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="logs" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Dispatch log</CardTitle>
              <CardDescription>Every reminder issued or skipped.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4 flex flex-wrap gap-2">
                <Input
                  placeholder="worker_id" value={logFilter.worker_id}
                  onChange={(e) => setLogFilter({ ...logFilter, worker_id: e.target.value })}
                  className="w-56"
                  data-testid="reminders-logs-filter-worker"
                />
                <Input
                  placeholder="outcome (e.g. sent)" value={logFilter.outcome}
                  onChange={(e) => setLogFilter({ ...logFilter, outcome: e.target.value })}
                  className="w-48"
                  data-testid="reminders-logs-filter-outcome"
                />
                <Input
                  placeholder="trigger_type" value={logFilter.trigger_type}
                  onChange={(e) => setLogFilter({ ...logFilter, trigger_type: e.target.value })}
                  className="w-48"
                  data-testid="reminders-logs-filter-trigger"
                />
                <Button variant="outline" onClick={() => loadLogs(1)} data-testid="reminders-logs-apply">
                  Apply
                </Button>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Worker</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Trigger</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Outcome</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logBusy && (
                    <TableRow><TableCell colSpan={6} className="py-6 text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></TableCell></TableRow>
                  )}
                  {!logBusy && logs.items.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="py-6 text-center text-muted-foreground">No log rows match.</TableCell></TableRow>
                  )}
                  {logs.items.map((row) => (
                    <TableRow key={row.id} data-testid={`log-row-${row.id}`}>
                      <TableCell className="text-xs text-muted-foreground font-mono">
                        {row.dispatched_at?.slice(0, 19).replace("T", " ")}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{row.worker_id.slice(0, 12)}…</TableCell>
                      <TableCell><Badge variant="outline">{row.channel}</Badge></TableCell>
                      <TableCell><Badge variant="outline">{row.trigger_type}</Badge></TableCell>
                      <TableCell className="text-xs">{row.period_week}</TableCell>
                      <TableCell>
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${outcomeColors[row.outcome] || ""}`}>
                          {row.outcome}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                <span>Page {logs.page} · showing {logs.items.length} of {logs.total}</span>
                <div className="space-x-2">
                  <Button size="sm" variant="outline" onClick={() => loadLogs(Math.max(1, logs.page - 1))} disabled={logs.page === 1} data-testid="reminders-logs-prev">
                    Prev
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => loadLogs(logs.page + 1)} disabled={logs.page * logs.page_size >= logs.total} data-testid="reminders-logs-next">
                    Next
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!confirmGate} onOpenChange={(open) => !open && setConfirmGate(null)}>
        <AlertDialogContent data-testid="reminders-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Send to {confirmGate?.target_count} workers?</AlertDialogTitle>
            <AlertDialogDescription>
              This dispatch would reach <strong>{confirmGate?.target_count}</strong> workers,
              which is over the current confirm threshold of <strong>{confirmGate?.max_without_confirm}</strong>.
              Reminders go out immediately on confirm — email + in-app — and get logged for audit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reminders-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doDispatch(true); }}
              data-testid="reminders-confirm-send"
            >
              Yes, send to all {confirmGate?.target_count}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
